import { useState, useEffect, SyntheticEvent } from "react";
import { Row, Col } from "react-bootstrap";
import { FormattedMessage, useIntl } from "react-intl";
import { useFormContext, useWatch } from "react-hook-form";

import { uploadFile, getFile } from "api";
import { generateUUID, promiseAllSettled } from "helpers/commonHelpers";
import Button from "components/atoms/Button/Button";
import errorToast from "helpers/errorToast";
import FileAttachmentItem from "pages/common/FileAttachmentItem/FileAttachmentItem";
import "../miscForm.scss";

interface File {
  id: string;
  url: string;
  filename?: string;
  name: string;
  file: string;
  size: number;
}

function Attachments({ quotationConfigData }: any) {
  const intl = useIntl();
  const { setValue } = useFormContext();
  const [attachments] = useWatch({
    name: ["additional_information.attachments"],
  });

  const [files, setFiles] = useState<{ [key: string]: File }>({});
  const [defaultFile, setDefaultFile] = useState<any>(null);
  const [initialFetchDone, setInitialFetchDone] = useState(false);

  useEffect(() => {
    const defaultAttachment = quotationConfigData?.assigned_team?.attachment;
    if (!defaultAttachment) return;

    const formatDefaultAttachment = (attachment: any) => ({
      [attachment.reference_number]: {
        file: attachment.reference_number,
        filename: attachment.filename,
        url: attachment.file_url,
      },
    });

    setDefaultFile(formatDefaultAttachment(defaultAttachment));
    if (!attachments || attachments.length === 0) {
      setValue("additional_information.attachments", [defaultAttachment.reference_number]);
    }
  }, [quotationConfigData]);

  // Prefill all files from draft
  useEffect(() => {
    if (!attachments || attachments.length === 0 || initialFetchDone) return;

    const fetchFiles = async () => {
      try {
        const responses = await Promise.all(
          attachments.map(async (file: any) => {
            // Check if file's reference_number matches default attachment's reference_number, in this case we do not fetch again.
            if (defaultFile && defaultFile[file]) {
              return {
                file,
                filename: defaultFile[file].filename,
                url: defaultFile[file].url,
              };
            }
            try {
              const response = await getFile(file);
              return {
                file,
                filename: response.data.name,
                url: response.data.url,
              };
            } catch (err) {
              return null;
            }
          }),
        );
        return responses;
      } catch (error) {
        errorToast(error);
        return [];
      }
    };

    fetchFiles()
      .then((files) => {
        const formattedFiles = files.reduce((acc, currentFile) => ({ ...acc, [currentFile.file]: currentFile }), {});
        setFiles(formattedFiles);
        // Combine formattedFiles with defaultFile, filter out duplicates
        const combinedAttachments = [...Object.keys(formattedFiles), ...Object.keys(defaultFile)];
        const uniqueAttachments = Array.from(new Set(combinedAttachments));
        setValue("additional_information.attachments", uniqueAttachments);
      })
      .finally(() => setInitialFetchDone(true));
  }, [attachments, initialFetchDone, defaultFile]);

  const handleFilesSelect = async (event: SyntheticEvent<HTMLInputElement, Event>) => {
    event.persist();
    const filesData: Record<string, any> = {};
    const { files: selectedFiles } = event.target as HTMLInputElement;
    if (!selectedFiles) return;

    Array.from(selectedFiles).forEach((file) => {
      const uuid = generateUUID();
      filesData[uuid] = {
        id: uuid,
        file,
        progress: 1,
        error: false,
        name: file.name,
        url: "",
        abortController: new AbortController(),
      };
    });
    setFiles((prev) => ({
      ...prev,
      ...filesData,
    }));

    const uploadingFiles = Object.values(filesData).map((f) => uploadFiles(f));

    // Filter out nulls
    const uploadedFiles = await promiseAllSettled(uploadingFiles.filter((nonNulls) => nonNulls));
    const successfullyUploadedFiles = uploadedFiles.filter((file) => file.status === "fulfilled").map((file) => file.value);

    const formattedFiles = successfullyUploadedFiles.reduce((acc, file) => {
      acc[file.reference_number] = {
        file: file.reference_number,
        filename: file.name,
        url: file.url,
      };
      return acc;
    }, {});

    const existingAttachments = Object.keys(files);
    const newAttachments = Object.keys(formattedFiles);
    setValue("additional_information.attachments", [...existingAttachments, ...newAttachments]);
  };

  const uploadFiles = (data: { file: File; id: string; name: string; abortController: AbortController }) => {
    if (!data.file) return;
    if (data.file.size >= 15_000_000) {
      updateFiles(data.id, {
        progress: null,
        error: true,
        errorMessage: intl.formatMessage({
          id: "new-request.attachments.attachmentFile.maxUploadSize",
          defaultMessage: "(max filesize 15mb)",
        }),
      });
      return;
    }

    const formData = new FormData();
    formData.append("file", data.file as any);
    formData.append("filename", data.name);

    return uploadFile(formData, {
      onUploadProgress: (progressEvent: ProgressEvent) => {
        updateFiles(data.id, {
          progress: Math.round((progressEvent.loaded * 100) / progressEvent.total),
        });
      },
      signal: data.abortController.signal,
    })
      .then((res: { data: { reference_number: string; name: string; url: string } }) => {
        setFiles((prev: Record<string, any>) => {
          const cloned = { ...prev };
          const updatedData = {
            file: res.data.reference_number,
            filename: res.data.name,
            url: res.data.url,
            progress: 0,
          };
          delete cloned[data.id];
          return {
            ...cloned,
            [updatedData.file]: updatedData,
          };
        });
        return Promise.resolve(res.data);
      })
      .catch((error: any) => {
        if (error?.code !== "ERR_CANCELED") errorToast(error, "Could not upload file, please try again later");
        updateFiles(data.id, { progress: 0, error: true });
        return Promise.reject(error);
      });
  };

  const updateFiles = (id: string, data: Object) => {
    setFiles((prev: any) => ({
      ...prev,
      ...(prev[id] && {
        [id]: {
          ...prev[id],
          ...data,
        },
      }),
    }));
  };

  const handleFileRemove = (data: File) => {
    setFiles((prev) => {
      const cloned = { ...prev };
      if (cloned[data.file]) {
        delete cloned[data.file];
      } else if (cloned[data.id]) {
        delete cloned[data.id];
      }
      return cloned;
    });
    if (attachments?.length) {
      setValue(
        "additional_information.attachments",
        attachments.filter((file: any) => file.file !== data.file),
      );
    }
  };

  return (
    <Row className="mb-24 misc-attachments">
      <Col lg={2} md={3}>
        <h6>
          <FormattedMessage id="new-request.attachments.attachment.attachments" defaultMessage="Attachments" />
        </h6>
      </Col>
      <Col lg={6} md={9}>
        {defaultFile && Object.keys(defaultFile).length > 0 && (
          <Row className="mb-2">
            <Col className="d-flex align-items-center">
              {Object.values(defaultFile).map((fileInfo: any) => (
                <a key={fileInfo.file} href={fileInfo.url} target="_blank" rel="noopener noreferrer">
                  {fileInfo.filename ?? fileInfo.name}
                </a>
              ))}
            </Col>
          </Row>
        )}
        {Object.values(files).map((file, i) => {
          if (defaultFile && defaultFile[file.file]) return null;
          return <FileAttachmentItem key={i} currentFile={file} onRemove={() => handleFileRemove(file)} />;
        })}
        <Button variant="primary" className="rounded-pill cursor-pointer blue-100 h-26 align-self-center position-relative">
          <FormattedMessage id="new-request.otherAttachment.addAttachment" defaultMessage="Add attachment" />
          <input
            type="file"
            accept="application/pdf, image/gif, image/tiff, text/csv, application/vnd.apple.pages, application/vnd.apple.keynote, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.presentationml.presentation, application/msword, application/vnd.ms-powerpoint, application/vnd.openxmlformats-officedocument.wordprocessingml.document, image/jpeg, image/png"
            className="attachment-input-file"
            multiple
            onChange={handleFilesSelect}
            onClick={(e: any) => {
              e.target.value = null;
            }}
          />
        </Button>
      </Col>
    </Row>
  );
}

export default Attachments;
